import { request } from "@/request";
import getBackendURL from "./api";


export const DASHBOARD_GROUPS = ["Admin", "RSU", "NRU", "CRU"];

export function isDashboardUser(groups: string[] | undefined, admin?: boolean) {
	if (admin) return true;
	return (groups ?? []).some((group) =>
		DASHBOARD_GROUPS.some((allowed) => group === allowed || group.startsWith(`${allowed} `)),
	);
}

/** Shared by both dashboards: route parts for /esr/:esrId/:activeLabel/:activeId */
export interface DashboardESRLink {
	esr_id: number;
	display_id: string;
	root_id: string;
	label: string;
}

export type LettingDateType = "letting" | "completion";

export interface LettingDashboardParams {
	date_type: LettingDateType;
	start?: string;
	end?: string;
	district?: string;
	sponsor_type?: string;
	migrated?: "" | "yes" | "no";
	completed?: "" | "yes" | "no";
	search?: string;
    /** Initial ESR # range; an addendum is in range when its initial ESR is */
	esr_from?: string;
	esr_to?: string;
	/** No filters chosen: backend returns only the latest non-migrated ESRs */
	default_view?: boolean;
	page: number;
	page_size: number;
}

export interface LettingDashboardRow extends DashboardESRLink {
	migrated: boolean;
	letting_date: string | null;
	completion_date: string | null;
	e5_date: string | null;
	completed: boolean;
	district: number | null;
	sponsor_type: string;
	units_required: string[];
}


export interface LettingDashboardResponse {
	total: number;
	rows: LettingDashboardRow[];
    default_view: boolean;
	default_view_limit: number;
	facets: { districts: number[]; sponsor_types: string[] };
}

export type ExpiryStatus = "sent" | "missed" | "skipped" | "upcoming";

export interface ExpiryDashboardParams {
	types: string[];
	start?: string;
	end?: string;
	status?: "" | ExpiryStatus;
	search?: string;
	page: number;
	page_size: number;
}

export interface ExpiryDashboardRow extends DashboardESRLink {
	id: number;
	question_id: string;
	type: string;
	actual_date: string | null;
	expiry_date: string;
	warning_date: string | null;
	warning_sent: boolean;
	expired_sent: boolean;
	status: ExpiryStatus;
	expired_days: number;
}

export interface ExpiryDashboardResponse {
	total: number;
	rows: ExpiryDashboardRow[];
	health: Record<string, Record<ExpiryStatus, number>>;
	types: string[];
}

// drop empty values so the backend applies its defaults
function cleanParams(params: object) {
	return Object.fromEntries(
		Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
	);
}

export async function fetchLettingDashboard(params: LettingDashboardParams) {
	const { data } = await request.get<LettingDashboardResponse>(
		getBackendURL("/esr/dashboard/letting/"),
		{ params: cleanParams(params) },
	);
	return data;
}

export async function fetchExpiryDashboard(params: ExpiryDashboardParams) {
	const { data } = await request.get<ExpiryDashboardResponse>(
		getBackendURL("/esr/dashboard/expiry/"),
		{ params: cleanParams({ ...params, types: params.types.join(",") }) },
	);
	return data;
}

export async function sendExpiryNotification(id: number) {
	const { data } = await request.post<{ status: string }>(
		getBackendURL("/esr/dashboard/expiry/send/"),
		{ id },
	);
	return data;
}